import { FC } from "react";
import { Box } from "@chakra-ui/react";
import { Header } from "@/components/Header";
import { Seo } from "@/components/Seo";
import { BreadcrumbSchema, ProductSchema } from "@/components/StructuredData";
import { PublicLayout } from "@/layouts/PublicLayout";
import { SUPPORTED_LOCALES, Locale } from "@/constants/landingPages";
import { ElementorContent } from "@/types/elementorContent";
import { LandingHeroSection } from "../LandingPage/sections/LandingHeroSection";
import { LandingFeaturesSection } from "../LandingPage/sections/LandingFeaturesSection";
import { LandingPricingSection } from "../LandingPage/sections/LandingPricingSection";
import { LandingHowItWorksSection } from "../LandingPage/sections/LandingHowItWorksSection";
import { LandingCtaSection } from "../LandingPage/sections/LandingCtaSection";
import { WordPressPainPointsSection } from "./sections/WordPressPainPointsSection";

interface ElementorLandingPageProps {
  locale: Locale;
  platform: string;
  content: ElementorContent;
}

export const ElementorLandingPage: FC<ElementorLandingPageProps> = ({ locale, platform, content }) => {
  const baseUrl = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk";
  const currentUrl = `${baseUrl}/${locale}/${platform}`;

  // Generate hreflang tags for all supported locales
  const hreflangs = SUPPORTED_LOCALES.map((loc) => ({
    locale: loc,
    url: `${baseUrl}/${loc}/${platform}`,
  }));

  return (
    <PublicLayout>
      <Seo
        title={content.meta.title}
        description={content.meta.description}
        ogTitle={content.meta.ogTitle}
        ogDescription={content.meta.ogDescription}
        url={currentUrl}
        canonical={currentUrl}
        hreflangs={hreflangs}
      />

      {/* Structured Data for SEO */}
      <BreadcrumbSchema locale={locale} platform={platform} />
      <ProductSchema platform={platform} locale={locale} />

      <Header bg="white" isWithBreadcrumbs={false} locale={locale} content={content} />
      <Box as="main">
        {/* Hero - WordPress plugin alternative messaging */}
        <LandingHeroSection content={content.hero} locale={locale} />

        {/* UNIQUE: Plugin comparison table - not in GoodBarber */}
        {/* <PluginComparisonSection content={content.pluginComparison} /> */}

        {/* UNIQUE: WordPress pain points - not in GoodBarber */}
        <WordPressPainPointsSection content={content.wordpressPainPoints} />

        {/* Features - WordPress-focused (no plugin conflicts, async loading, etc.) */}
        <LandingFeaturesSection content={content.features} />

        {/* Pricing with WordPress plugin cost comparison */}
        <Box id="pricing">
          <LandingPricingSection content={content.pricing} />
        </Box>

        {/* How it works - WordPress/Elementor specific steps */}
        <LandingHowItWorksSection content={content.howItWorks} />

        {/* Final CTA - "Ditch WordPress plugins" messaging */}
        <LandingCtaSection content={content.finalCta} locale={locale} />
      </Box>
    </PublicLayout>
  );
};
